/* eslint-disable import/order */
import React from 'react';
import { projectSettingsCoreIssueTypesWorkflowRoute } from '@jira/router-routes__project-settings-workflow-editor-routes/src/projectSettingsCoreIssueTypesWorkflowRoute.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdProjectSettings } from '@jira/navigation-apps__constants/src/SidebarIdProjectSettings.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import { getBusinessProjectResources } from '@jira/router-resources__business-common/src/getBusinessProjectResources.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import { licenseDetailsResource_DEPRECATED_DO_NOT_USE } from '@jira/router-resources__business-license-details/src/useIsPremiumUserSeat.tsx';
import { workflowBuilderProvisioningResource } from '@jira/router-resources__admin-pages-workflows-workflow-builder-provisioning/src/useWorkflowBuilderProvisioning.tsx';
import { workflowDocumentResource } from '@jira/router-resources__admin-pages-workflows-workflow-document/src/workflowDocumentResource.tsx';
import { workflowCapabilitiesResource } from '@jira/router-resources__admin-pages-workflows-workflow-capabilities/src/workflowCapabilitiesResource.tsx';
import type WorkflowNavigationSettingsType from '@jira/spa-apps__project-settings-workflow-navigation/src/WorkflowNavigationPage.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { coreProjectLayout } from '@jira/platform__layouts-core-project-layout/src/index.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { jiraSpaResources } from '@jira/router-resources__spa-page-container/src/jiraSpaResources.tsx';
import { rovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { chromelessLayout } from '@jira/platform__layouts-chromeless-layout/src/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { projectWorkflowContextResource } from '@jira/router-resources__project-workflow-context/src/projectWorkflowContextResource.tsx';
import { getNavigationSidebarProjectSettingsResource } from '@jira/router-routes__project-settings-core-entries/src/common/getNavigationSidebarProjectSettingsResource.tsx';
import type { Route } from '@atlassian/react-resource-router';

const LazyWorkflowNavigationSettings = lazyForPaint<typeof WorkflowNavigationSettingsType>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-project-settings-workflow-navigation", jiraSpaEntry: "async-project-settings-workflow-navigation" */ '@jira/spa-apps__project-settings-workflow-navigation/src/WorkflowNavigationPage.tsx'
		),
	),
);

const WorkflowNavigationPage = () => (
	<LazyPage
		Page={LazyWorkflowNavigationSettings}
		pageId="project-settings-workflow-navigation"
		shouldShowSpinner={false}
	/>
);

const getWorkflowBuilderProvisioningResources = () => [workflowBuilderProvisioningResource];

export const projectSettingsCoreIssueTypesWorkflowRouteEntry: Route = createEntry(
	projectSettingsCoreIssueTypesWorkflowRoute,
	() => ({
		group: 'project-settings-core',

		navigation: expValEquals(
			'hix-7127_wfe_layout_to_chromeless_for_business',
			'isEnabled',
			true,
			false,
		)
			? undefined
			: {
					menuId: MenuIdProjects,
					sidebarId: SidebarIdProjectSettings,
				},

		layout: expValEquals('hix-7127_wfe_layout_to_chromeless_for_business', 'isEnabled', true, false)
			? chromelessLayout
			: coreProjectLayout,

		resources: expValEquals(
			'hix-7127_wfe_layout_to_chromeless_for_business',
			'isEnabled',
			true,
			false,
		)
			? [
					// Delete this resource after hix-7127_wfe_layout_to_chromeless_for_business experiment is over
					// AND usages of projectContextResource have been swapped with projectWorkflowContextResource
					projectContextResource,
					projectWorkflowContextResource,
					workflowCapabilitiesResource,
					workflowDocumentResource,
					...jiraSpaResources(),
					rovoEntitlementResource,
					...getWorkflowBuilderProvisioningResources(),
				]
			: [
					...getNavigationResources(),
					...getWorkflowBuilderProvisioningResources(),
					// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
					// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
					projectContextResource,
					forgeProjectModuleResource,
					staticProjectSidebarNavigationResource,
					staticProjectSidebarConnectAddonsNavigationResource,
					// end of resources expanded from getNavigationSidebarProjectResource
					...getNavigationSidebarProjectSettingsResource(),
					...getBusinessProjectResources(),
					licenseDetailsResource_DEPRECATED_DO_NOT_USE,
				],

		// eslint-disable-next-line no-restricted-syntax
		component: WorkflowNavigationPage,

		forPaint: expValEquals(
			'hix-7127_wfe_layout_to_chromeless_for_business',
			'isEnabled',
			true,
			false,
		)
			? [LazyWorkflowNavigationSettings]
			: [LazyAtlassianNavigation, LazyWorkflowNavigationSettings],

		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
